import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { legacyConvertCurrency } from 'utils/general';

import DateTimeIcon from 'assets/images/calendar/icon-datetime.svg';
import AlertIcon from 'assets/images/calendar/icon-alert.svg';
import AmountDueIcon from 'assets/images/calendar/icon-amount-due.svg';
import LeadIcon from 'assets/images/calendar/icon-lead.svg';
import ProjectIcon from 'assets/images/calendar/icon-project.svg';
import ServiceTypeIcon from 'assets/images/calendar/icon-service-type.svg';
import VendorIcon from 'assets/images/calendar/icon-vendor.svg';

const PaymentEventCard = ({ eventDetails, userDetails }) => {
  const {
    id: paymentId,
    extendedProps = {},
  } = eventDetails || {};
  // TODO: We will return to these later
  // They should be provided with a more consistent pattern
  // const { image306 } = project || {};
  const { dateFormat } = userDetails || {};

  const {
    weddingId: projectId,
    weddingName = '',
    weddingClientName = '',
    leadName = '',
    leadStatus = '',
    projectName = '',
    vendorName = '',
    vendorCategories = [],
    dependsOnFinalGuestCount: isPendingFinalGuestCount = false,
    dueDate = null,
    paidOn = null,
    amount = null,
    weddingCurrency = 'USD',
    invoicePayments = [],
    scheduledPayments = [],
    paymentType = '',
    invoiceIdentifier = '',
    invoiceName = '',
    organizationId = null,
    invoiceId = null,
  } = extendedProps;

  const isInvoicePayment = paymentType === 'invoice';
  const paymentsList = isInvoicePayment ? scheduledPayments : invoicePayments;

  const paymentIndex = (() => {
    const index = paymentsList.findIndex(p => p.id === Number(paymentId));
    return (index !== -1) ? index + 1 : null;
  })();
  const paymentTotal = paymentsList?.length || 0;

  const showPaymentInfo = amount !== null && paymentIndex && paymentTotal > 0;
  const formattedAmount = amount !== null
    ? legacyConvertCurrency(amount, weddingCurrency)
    : '-';
  const displayProjectName = projectId ? weddingName : projectName;
  const invoiceNumber = invoiceIdentifier ? `INV${invoiceIdentifier}` : '';

  const paymentsRows = useMemo(() => {
    // Vendor payments just show Paid or Due date
    if (!isInvoicePayment) {
      return paidOn
        ? [{ label: 'Paid on', date: paidOn }]
        : [{ label: 'Due on', date: dueDate }];
    }

    const currentPaymentId = Number(paymentId);
    let expectedScheduledPaymentsTotal = 0;
    let invoicePaid = 0;
    for (let scheduledPaymentIndex = 0; scheduledPaymentIndex < scheduledPayments.length; scheduledPaymentIndex+=1) {
      const scheduledPayment = paymentsList[scheduledPaymentIndex];
      if (scheduledPaymentIndex < paymentIndex) {
        expectedScheduledPaymentsTotal += Number(scheduledPayment.amount) || 0;
      }
    }
    for (let invoicePaymentIdx = 0; invoicePaymentIdx < invoicePayments.length; invoicePaymentIdx+=1) {
      const invoicePayment = invoicePayments[invoicePaymentIdx];
      invoicePaid += Number(invoicePayment.amount) || 0;
    }

    const calculatedRows = [];
    const remainingScheduled = expectedScheduledPaymentsTotal - invoicePaid;

    // Iterate through the scheduled payments (paymentsList) and allocate invoicePayments in sequence.
    // This ensures we correctly match partial payments to their scheduled payments
    // and stop once we fully process the scheduled payment corresponding to paymentId.
    let prevScheduledAmount = 0;
    paymentsList.some((payment) => {
      const isCurrentPayment = payment.id === currentPaymentId;
      const currentExpectedAmount = Number(payment.amount) || 0;

      // We need to check that the total invoicePaid is
      // either higher or lower than current scheduled amount
      // if higher:
      // -- mark as paid in total with currentExpectedAmount
      //    with latest date of invoicePayment that sums to currentExpectedAmount
      // if lower:
      // -- subtract previous scheduled amounts from invoicePaid
      //    this will represent amountInvoicePaidInCurrentPayment
      const today = null;
      const currentScheduledPaid = invoicePaid - prevScheduledAmount;
      if (isCurrentPayment && invoicePaid > prevScheduledAmount + currentExpectedAmount) {
        const latestDateOfRelativeInvoicePaymentSum = invoicePayments.reduce((acc, ip) => {
          const ipAmount = Number(ip.amount) || 0;
          acc.sum += ipAmount;

          // It's possible to make payments greater than a scheduled payment
          // therefore this won't always sum to the current expected amount
          // and may actually go over
          if (acc.sum >= currentExpectedAmount) {
            acc.latestDate = ip?.paid_at || null;
            acc.gate = true;
          }
          if (acc.gate && acc.sum < currentExpectedAmount) {
            acc.gate = false;
          }

          return acc;
        }, { sum: 0, gate: false, latestDate: today });
        calculatedRows.push({
          label: 'Paid on',
          date: latestDateOfRelativeInvoicePaymentSum.latestDate,
          amount: currentExpectedAmount,
        });
      } else if (isCurrentPayment && currentScheduledPaid > 0) {
        const latestInvoicePaymentDate = invoicePayments[invoicePayments.length-1].paid_at;
        calculatedRows.push({
          label: 'Paid on',
          date: latestInvoicePaymentDate,
          amount: currentScheduledPaid,
        });
      }

      prevScheduledAmount += currentExpectedAmount;

      if (isCurrentPayment) {
        if (remainingScheduled > 0) {
          calculatedRows.push({
            label: 'Due on',
            date: payment?.due_date ?? null,
            amount: remainingScheduled,
          });
        }
        return true;
      }

      return false;
    });

    return calculatedRows;
  }, [paymentType, paymentId, paidOn, dueDate, paymentsList, invoicePayments]);

  return (
    <div className="event-card info-card">
      <div className="info-card__content">
        <div className="info-card__row">
          <div className="info-card__title ap-text">
            { isInvoicePayment ? 'AP Invoice Payment Due' : 'Vendor Payment Due' }
          </div>
        </div>

        {isInvoicePayment && (
          <div className="info-card__row">
            <div className="info-card__icon-wrapper">
              <img
                className="info-card__icon"
                src={LeadIcon}
                alt="lead icon"
              />
            </div>
            <div className="info-card__lead">
              <div className="info-card__lead-header">
                <span className="info-card__lead-name">
                  {leadName || weddingClientName || '-'}&nbsp;
                </span>
                <span className="info-card__lead-status">
                  {leadStatus}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={ProjectIcon}
              alt="service type icon"
            />
          </div>
          <div>{displayProjectName || '-'}</div>
        </div>

        {!isInvoicePayment && (
          <div className="info-card__row">
            <div className="info-card__icon-wrapper">
              <img
                className="info-card__icon"
                src={VendorIcon}
                alt="service type icon"
              />
            </div>
            <div>{vendorName || '-'}</div>
          </div>
        )}

        {
            // Hide service type for invoice payments
            // TODO: This should be handled more generically
            !isInvoicePayment && (
            <div className="info-card__row">
              <div className="info-card__icon-wrapper">
                <img
                  className="info-card__icon"
                  src={ServiceTypeIcon}
                  alt="service type icon"
                />
              </div>
              <div>
                {vendorCategories.length > 0
                  ? vendorCategories.map(c => c.category).join(', ')
                  : '-'
                }
              </div>
            </div>
          )
        }

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={AmountDueIcon}
              alt="service type icon"
            />
          </div>
          <div>
            {isInvoicePayment && (
              <>
                <div>{invoiceNumber || '-'}</div>
                <div>{invoiceName || '-'}</div>
              </>
            )}
            <div>
              {formattedAmount}
              {showPaymentInfo && ` (${paymentIndex} of ${paymentTotal} payments)`}
            </div>
          </div>
        </div>

        {!isInvoicePayment && (
          <div className="info-card__row">
            <div className="info-card__icon-wrapper">
              <img
                className="info-card__icon"
                src={AlertIcon}
                alt="service type icon"
              />
            </div>
            <div>
              {isPendingFinalGuestCount ? 'Pending Final Guest Count' : '-'}
            </div>
          </div>
        )}

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={DateTimeIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            {paymentsRows.map((payment, index) => (
              <div key={index}>
                {isInvoicePayment && paymentsRows.length > 1 && payment.amount != null && (
                  <>
                    {legacyConvertCurrency(payment.amount, weddingCurrency)}&nbsp;
                  </>
                )}
                {payment.label} {payment.date ? dayjs(payment.date).format(dateFormat) : '-'}
              </div>
            ))}
          </div>
        </div>
      </div>

      {projectId && (
        <a
          className="button button--sm"
          href={`/app/project/${projectId}/tools/contacts`}
        >
          View Contact Record
        </a>
      )}

      {invoiceId && (
        <a
          className="button button--sm"
          href={`/app/pro/${organizationId}/invoices/${invoiceId}?return_to=calendar`}
        >
          View Invoice
        </a>
      )}
    </div>
  );
};

export default PaymentEventCard;
